import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Server,
  Database,
  Gauge,
  Settings,
  Activity
} from 'lucide-react';

const BACKEND_URL = window.__BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SystemSettings() {
  const [health, setHealth] = useState({
    backend_status: 'healthy',
    database_status: 'healthy',
    last_checked: new Date().toISOString()
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadHealth = async () => {
      try {
        const res = await fetch(`${API}/admin/system-health`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setHealth({
            backend_status: data.backend_status || 'healthy',
            database_status: data.database_status || 'healthy',
            last_checked: new Date().toISOString()
          });
        }
      } catch (error) {
        // fall back to defaults
      } finally {
        setLoading(false);
      }
    };

    loadHealth();
  }, []);

  const getHealthBadge = (status) => {
    if (status === 'healthy') return <Badge className="border-green-500/30 text-green-400">Healthy</Badge>;
    if (status === 'warning') return <Badge className="border-yellow-500/30 text-yellow-400">Warning</Badge>;
    return <Badge className="border-red-500/30 text-red-400">Degraded</Badge>;
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl space-y-6">
      <div className="mb-2">
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-2">
          <Server className="h-8 w-8 text-blue-500" />
          System Settings Hub
        </h1>
        <p className="text-[#8A93A6]">Centralized configuration for platform health</p>
      </div>

      <Card className="glass">
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-green-500" />
              System Health Snapshot
            </CardTitle>
            <CardDescription>Live checks feed System Health in Admin Overview</CardDescription>
          </div>
          <p className="text-xs text-[#8A93A6]">Last checked: {new Date(health.last_checked).toLocaleString()}</p>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="p-4 rounded-lg bg-white/5 border border-white/10 flex items-center justify-between">
            <span className="text-sm text-[#8A93A6]">Backend</span>
            {getHealthBadge(health.backend_status)}
          </div>
          <div className="p-4 rounded-lg bg-white/5 border border-white/10 flex items-center justify-between">
            <span className="text-sm text-[#8A93A6]">Database</span>
            {getHealthBadge(health.database_status)}
          </div>
          <div className="p-4 rounded-lg bg-white/5 border border-white/10 flex items-center justify-between">
            <span className="text-sm text-[#8A93A6]">Execution</span>
            <Badge className="border-blue-500/30 text-blue-400">Read-only</Badge>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="glass">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Server className="h-5 w-5 text-blue-400" />
              Server Configuration
            </CardTitle>
            <CardDescription>API endpoints, domains, networking</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-[#8A93A6]">
            <p>Review server URLs, CORS, and auth callback settings.</p>
            <p>Ensure health checks are reachable and SSL is configured.</p>
          </CardContent>
        </Card>

        <Card className="glass">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Database className="h-5 w-5 text-emerald-400" />
              Database Configuration
            </CardTitle>
            <CardDescription>Connections, replicas, and backups</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-[#8A93A6]">
            <p>Validate connection strings, credentials, and replica lag.</p>
            <p>Backups and restore runbooks documented.</p>
          </CardContent>
        </Card>

        <Card className="glass">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Gauge className="h-5 w-5 text-orange-400" />
              Performance Configuration
            </CardTitle>
            <CardDescription>Caching, queues, and rate limits</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-[#8A93A6]">
            <p>Configure cache TTLs, worker concurrency, and queue alerts.</p>
            <p>Rate limits and circuit breakers documented.</p>
          </CardContent>
        </Card>

        <Card className="glass">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Settings className="h-5 w-5 text-gray-300" />
              General Configuration
            </CardTitle>
            <CardDescription>Feature flags, branding, routing</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-[#8A93A6]">
            <p>Central hub for platform-wide toggles and routing rules.</p>
            <p>Ensure admin defaults align with Phase 0 readiness.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
